import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import styles from "../styles.module.css";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomDatePicker } from "../../../../components";

const isFullyValidDate = (d) => {
  if (!d || typeof d.isValid !== "function" || !d.isValid()) return false;
  const year = d.year();
  return year >= 1900 && year <= 2100 && String(year).length === 4;
};

function ListRemedial() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const user_id = user?.id || "";
  const academic_id = clientData?.academic_year_id || "";
  // const schoolId =
  //   clientData.school_id != null && clientData.school_id !== ""
  //     ? Number(clientData.school_id)
  //     : null;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnFilters, setColumnFilters] = useState([]);

  // Individual column search states (each matches an API payload key)
  const [titleSearch, setTitleSearch] = useState("");
  const [topicSearch, setTopicSearch] = useState("");
  const [subTopicSearch, setSubTopicSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");
  const [questionsCountSearch, setQuestionsCountSearch] = useState("");
  const [createdDateSearch, setCreatedDateSearch] = useState("");
  const [statusSearch, setStatusSearch] = useState("");

  // Map of column id -> setter — used to clear individual search state
  // when the user clicks the X (clear) button on a column filter.
  const filterSetters = useMemo(
    () => ({
      title: setTitleSearch,
      topic: setTopicSearch,
      sub_topic: setSubTopicSearch,
      subject: setSubjectSearch,
      questions_count: setQuestionsCountSearch,
      created_date: setCreatedDateSearch,
      status: setStatusSearch,
    }),
    [],
  );

  // ==================== FETCH ====================
  const fetchListRemedial = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        user_id,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        title: titleSearch,
        topic: topicSearch,
        sub_topic: subTopicSearch,
        subject: subjectSearch,
        num_questions: questionsCountSearch,
        Medium: "",
        status: statusSearch,
        difficulty_level: "",
        created_date: createdDateSearch,
        search: "",
        academic_id: academic_id,
      };

      const response = await AxiosInstance.post(
        `/book/student/remedialList`,
        payload,
      );

      if (response?.data?.status) {
        const transformedData = (response.data.data || []).map(
          (item, index) => ({
            id: item.id,
            s_no: pagination.pageIndex * pagination.pageSize + index + 1,
            title:
              item.title ||
              (item.remedial_code && item.remedial_name
                ? `${item.remedial_code}-${item.remedial_name}`
                : item.remedial_name || "-"),
            topic: item.topic_name || item.topic || "-",
            sub_topic: item.sub_topic_name || item.sub_topic || "-",
            subject: item.subject_name || item.subject || "-",
            questions_count: item.num_questions ?? 0,
            created_date: item.created_at
              ? dayjs(item.created_at).format("DD/MM/YYYY")
              : "-",
            status: item.status || "Pending",
            parent_assessment_id: item.parent_assessment_id || null,
            client_id: item.client_id,
            school_id: item.school_id,
            class_id: item.class_id,
            section_id: item.section_id,
            isActive: item.isActive,
          }),
        );

        setData(transformedData);
        setRowCount(response.data.pagination?.total || transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching remedial assessment list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    user_id,
    pagination.pageIndex,
    pagination.pageSize,
    titleSearch,
    topicSearch,
    subTopicSearch,
    subjectSearch,
    questionsCountSearch,
    createdDateSearch,
    statusSearch,
  ]);

  useEffect(() => {
    fetchListRemedial();
  }, [fetchListRemedial]);

  // ==================== NAVIGATION ====================
  const handleStartRemedial = (row) => {
    navigate("/user/ViewRemedialTest", {
      state: { ID: row.id, data: row, mode: "start", type: "remedial" },
    });
  };

  const handleViewResult = (row) => {
    navigate("/user/viewStudentRemedialResult", {
      state: { ID: row.id, data: row, mode: "result", type: "remedial" },
    });
  };

  // ==================== FILTER HANDLERS ====================
  const handleColumnFiltersChange = (updaterOrValue) => {
    const newFilters =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;

    Object.keys(filterSetters).forEach((columnId) => {
      const oldFilter = columnFilters.find((f) => f.id === columnId);
      const newFilter = newFilters.find((f) => f.id === columnId);

      const oldValue = oldFilter?.value ?? "";
      const newValue = newFilter?.value ?? "";

      if (oldValue !== "" && newValue === "") {
        filterSetters[columnId]("");
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
    });

    setColumnFilters(newFilters);
  };

  const makeFilterKeyDownHandler = (setter) => (e) => {
    if (e.key === "Enter") {
      const inputValue = e.target.value || "";
      setter(inputValue);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  // ==================== STATUS BADGE ====================
  const renderStatusBadge = (status) => {
    const value = (status || "").toString().toLowerCase();
    let bgColor = "#FEE2E2";
    let textColor = "#B91C1C";
    let label = status || "-";

    if (value === "pending") {
      bgColor = "#FFE7CC";
      textColor = "#D97706";
      label = "Pending";
    } else if (value === "pass") {
      bgColor = "#D1FADF";
      textColor = "#15803D";
      label = "Pass";
    } else if (value === "fail") {
      bgColor = "#FEE2E2";
      textColor = "#B91C1C";
      label = "Fail";
    }

    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 14px",
          borderRadius: "6px",
          backgroundColor: bgColor,
          color: textColor,
          fontSize: "13px",
          fontWeight: 500,
          textAlign: "center",
          minWidth: "70px",
        }}
      >
        {label}
      </span>
    );
  };

  // ==================== ACTION BUTTON ====================
  const renderActionButton = (row) => {
    const status = (row.status || "").toString().toLowerCase();

    if (status === "pending") {
      return (
        <button
          onClick={() => handleStartRemedial(row)}
          style={{
            backgroundColor: "#F97316",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            padding: "8px 16px",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {t?.startAssessment || "Start Assessment"}
        </button>
      );
    }

    return (
      <button
        onClick={() => handleViewResult(row)}
        style={{
          backgroundColor: "#0F7A4F",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          padding: "8px 20px",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {t?.result || "Result"}
      </button>
    );
  };

  // ==================== SINGLE DATE FILTER ====================
  const SingleDateFilter = ({ column, translations: trans, onDateChange }) => {
    const filterValue = column.getFilterValue();

    const [draftValue, setDraftValue] = useState(
      filterValue ? dayjs(filterValue) : null,
    );

    useEffect(() => {
      setDraftValue(filterValue ? dayjs(filterValue) : null);
    }, [filterValue]);

    const handleChange = (newValue) => {
      setDraftValue(newValue);

      if (!newValue && filterValue) {
        column.setFilterValue(undefined);
        if (typeof onDateChange === "function") onDateChange("");
      }
    };

    const handleAccept = (newValue) => {
      if (!newValue) {
        column.setFilterValue(undefined);
        if (typeof onDateChange === "function") onDateChange("");
        return;
      }

      if (isFullyValidDate(newValue)) {
        const formatted = newValue.format("YYYY-MM-DD");
        column.setFilterValue(formatted);
        if (typeof onDateChange === "function") onDateChange(formatted);
      }
    };

    return (
      <CustomDatePicker
        name={`${column.id}_date`}
        value={draftValue}
        onChange={handleChange}
        onAccept={handleAccept}
        placeholder={trans?.labels?.createdDate || "Created Date"}
        format="DD/MM/YYYY"
        size="sm"
        isClearable={true}
        minDate={dayjs("1900-01-01")}
        maxDate={dayjs("2100-12-31")}
      />
    );
  };

  // ==================== COLUMNS ====================
  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: t?.title || "Title",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t?.title || "Title",
          onKeyDown: makeFilterKeyDownHandler(setTitleSearch),
        },
      },
      {
        accessorKey: "topic",
        header: t?.topic || "Topic",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t?.topic || "Topic",
          onKeyDown: makeFilterKeyDownHandler(setTopicSearch),
        },
      },
      {
        accessorKey: "sub_topic",
        header: t?.subTopic || "Sub Topic",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t?.subTopic || "Sub Topic",
          onKeyDown: makeFilterKeyDownHandler(setSubTopicSearch),
        },
      },
      {
        accessorKey: "subject",
        header: t?.subject || "Subject",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t?.subject || "Subject",
          onKeyDown: makeFilterKeyDownHandler(setSubjectSearch),
        },
      },
      {
        accessorKey: "questions_count",
        header: t?.questionCount || "Questions Count",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t?.questionCount || "Questions Count",
          onKeyDown: makeFilterKeyDownHandler(setQuestionsCountSearch),
        },
      },
      {
        accessorKey: "created_date",
        header: t?.createdDate || "Created Date",
        enableSorting: true,
        enableColumnFilter: true,
        Filter: ({ column }) => (
          <SingleDateFilter
            column={column}
            translations={translations}
            onDateChange={(formatted) => {
              setCreatedDateSearch(formatted);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        ),
        Cell: ({ cell }) => cell.getValue() || "-",
        size: 200,
      },
      {
        accessorKey: "status",
        header: t?.status || "Status",
        enableSorting: true,
        enableColumnFilter: true,
        muiFilterTextFieldProps: {
          placeholder: t?.status || "Status",
          onKeyDown: makeFilterKeyDownHandler(setStatusSearch),
        },
        Cell: ({ row }) => renderStatusBadge(row.original.status),
      },
      {
        id: "actions",
        header: t?.actions || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => renderActionButton(row.original),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, translations],
  );

  return (
    <div className={styles.container}>
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        enableSearch={false}
      />
    </div>
  );
}

export default ListRemedial;
